import { FC, ReactNode } from 'react';
// Bell, Layers, Users — kept commented out alongside Active Users / Nurture / Search Alerts widgets (re-add later)
import {
  Bot,
  CalendarCheck,
  Clock,
  Flame,
  Globe,
  Mail,
  MessageSquare,
  MousePointer,
  Phone,
  PhoneForwarded,
  Target,
  UserPlus,
  Zap,
} from 'lucide-react';
import { formatNumber } from '../../utils/format';
import { StatCardProps } from './StatCard';
import { AvailableWidget } from './AddWidgetCard';
import type { PinnedWidget } from '../../stores/usePinnedWidgets';
import type { GQLOrganizationStats } from '../../hooks/graphql/organizations/useOrganization';
import type {
  FollowUpStatsByStrategy,
  FollowUpStrategySegment,
  FollowUpTypeBreakdown,
} from '../../api/nextFollowUp';

/**
 * The Add-Widget library entries.
 *
 * Two of these describe a card whose contents depend on the entitlement, so the
 * copy has to follow: Remi Touches counts SMS only for a Flywheel org, and the
 * Calls card headlines Conversations there and Opportunities everywhere else.
 * Sibling of `getDefaultOrder` / `getLockedWidgets`, which take the same flag.
 */
export const getAllWidgets = (flywheelEnabled: boolean): AvailableWidget[] => [
  {
    id: 'flywheel',
    label: 'Flywheel',
    description: "Remi's automated follow-ups",
    icon: <Zap className="w-4 h-4" />,
  },
  {
    id: 'leads',
    label: 'New Leads',
    description: 'New leads from email, organic, and PPC sources',
    icon: <UserPlus className="w-4 h-4" />,
  },
  {
    id: 'calls',
    label: 'Calls',
    // Says what each stat counts and names it, since the labels alone do not
    // explain themselves. Calls Made has no direction filter server-side, so
    // it is every call on the org, not just the ones Remi placed.
    description: flywheelEnabled
      ? 'Conversations (calls a person answered) and Calls Made (every call, inbound and outbound)'
      : 'Opportunities (nurture or follow-up), Calls Made (every call), and Conversations (answered)',
    icon: <Phone className="w-4 h-4" />,
  },
  {
    id: 'handoffs',
    label: 'Handoffs',
    description: 'Pending, claimed, returned, and expired handoffs',
    icon: <Flame className="w-4 h-4" />,
  },
  {
    id: 'live_transfers',
    label: 'Live Transfers',
    description: 'Direct handoffs to agents',
    icon: <PhoneForwarded className="w-4 h-4" />,
  },
  {
    id: 'email',
    label: 'Emails',
    description: 'Email deliveries, opens, and clicks',
    icon: <Mail className="w-4 h-4" />,
  },
  {
    id: 'website',
    label: 'Website',
    description: 'Traffic, property views, and searches',
    icon: <Globe className="w-4 h-4" />,
  },
  // GQL-powered widgets
  // { id: 'page_views', label: 'Page Views', description: 'Properties, searches, home valuations, and saved properties', icon: <MousePointer className="w-4 h-4" /> }, // TODO: same data as Website — revisit
  // COMMENTED OUT — Active Users / Nurture / Search Alerts widgets (re-add later)
  // { id: 'active_users', label: 'Active Users', description: 'Unique contacts answering calls, opening emails, and clicking properties', icon: <Users className="w-4 h-4" /> },
  {
    id: 'remi_touches',
    label: 'Remi Touches',
    description: flywheelEnabled
      ? 'Total client touches made by Remi — calls, SMS, emails, and web activity'
      : 'Total client touches made by Remi — calls, emails, and web activity',
    icon: <Bot className="w-4 h-4" />,
  },
  {
    id: 'sms',
    label: 'SMS',
    description: 'Text messages sent and received by Remi',
    icon: <MessageSquare className="w-4 h-4" />,
  },
  // Appointments hidden — no live aggregate from the API yet.
  // { id: 'appointments', label: 'Appointments', description: 'Tours and listing appointments scheduled', icon: <CalendarCheck className="w-4 h-4" /> },
  // { id: 'nurture', label: 'Nurture Pipeline', description: 'Clients currently in call and email nurture flows', icon: <Layers className="w-4 h-4" /> },
  // { id: 'search_alerts', label: 'Search Alerts', description: 'Search alerts created and searches made by users', icon: <Bell className="w-4 h-4" /> },
];

// eslint-disable-next-line react-refresh/only-export-components
const PreviewContent: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="p-3 space-y-2 text-sm">{children}</div>
);

// eslint-disable-next-line react-refresh/only-export-components
const PreviewRow: FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-muted-foreground text-xs">{label}</span>
    <span className="font-semibold text-foreground text-xs">
      {typeof value === 'string' ? value : formatNumber(value)}
    </span>
  </div>
);

export interface FollowUpData {
  total: number;
  uniqueContacts: number;
  outboundSms: number;
  calls: number;
  texts: number;
  emails: number;
  timeSaved: string;
  types: FollowUpTypeBreakdown[];
  byStrategy?: FollowUpStatsByStrategy;
}

export type FlywheelDrilldownFilter =
  | { kind: 'contacts'; title: string }
  | {
      kind: 'followups';
      title: string;
      decisionType?: string;
      strategySegment?: FollowUpStrategySegment;
    };

export interface DashboardWidgetData {
  stats: {
    leads: { value: number; email: number; website: number; ppc: number };
    calls: number;
    opportunities?: { value: number };
    liveTransfers: number;
    emails: number;
    orgStats?: GQLOrganizationStats | null;
  };
  leads?: unknown[];
}

export interface WebsiteWidgetData {
  stats: { propertyViews: number; searches: number };
}

export interface WidgetMapConfig {
  dashboardData: DashboardWidgetData | null | undefined;
  isDataLoading: boolean;
  /** When true, the Calls widget headlines Conversations instead of
   *  Opportunities, and the standalone Opportunities widget is filtered
   *  out by DashboardStats. */
  flywheelEnabled?: boolean;
  opportunities: number;
  conversations: number;
  handoffStats: {
    pending: number;
    claimed: number;
    released: number;
    declined: number;
    expired: number;
  };
  handoffLoading: boolean;
  webActivitiesTotal: number;
  webActivitiesLoading: boolean;
  propertyViewsTotal: number;
  leadCapturesTotal: number;
  emailDeliveredTotal: number;
  emailOpensTotal: number;
  emailClicksTotal: number;
  smsTotal: number;
  smsLoading: boolean;
  followUpData: FollowUpData | null;
  followUpLoading: boolean;
  orgStats: GQLOrganizationStats | null;
  orgStatsLoading: boolean;
  liveTransfersTotal: number;
  liveTransfersLoading: boolean;
  onShowLeads: () => void;
  onShowLeadsBySource: (source: string) => void;
  onShowCalls: () => void;
  onShowConversations: () => void;
  onShowOpportunities: () => void;
  onShowHandoffs: () => void;
  onShowClaimedHandoffs: () => void;
  onShowReturnedHandoffs: () => void;
  onShowDeclinedHandoffs: () => void;
  onShowExpiredHandoffs: () => void;
  onShowLiveTransfers: () => void;
  onShowEmailOpens: () => void;
  onShowEmailEngagement: (eventType: 'open' | 'click') => void;
  onShowSms: () => void;
  onShowWebsiteStats: () => void;
  onShowPropertyViews: () => void;
  onShowLeadCaptures: () => void;
  onShowPageViews: () => void;
  opportunityCallsTotal: number;
  opportunityCallsLoading: boolean;
  onShowOpportunityCalls: () => void;
  onShowActiveUsers: (
    filter?: 'answeredCalls' | 'openedEmails' | 'clickedEmails' | 'clickedProperties'
  ) => void;
  onShowNurture?: (filter?: 'callNurture' | 'emailNurture') => void;
  onShowSearchAlerts?: (filter?: 'alertContacts' | 'searches') => void;
  onShowFlywheelFollowUps?: (filter: FlywheelDrilldownFilter) => void;
}

export function buildWidgetMap(
  config: WidgetMapConfig
): Record<string, { card: StatCardProps; preview: ReactNode }> {
  const {
    dashboardData,
    isDataLoading,
    flywheelEnabled,
    opportunities,
    conversations,
    handoffStats,
    handoffLoading,
    webActivitiesTotal,
    webActivitiesLoading,
    propertyViewsTotal,
    leadCapturesTotal,
    emailDeliveredTotal,
    emailOpensTotal,
    emailClicksTotal,
    smsTotal,
    smsLoading,
    onShowSms,
    followUpData,
    followUpLoading,
    orgStats,
    orgStatsLoading,
    liveTransfersTotal,
    liveTransfersLoading,
    opportunityCallsTotal,
    opportunityCallsLoading,
    onShowOpportunityCalls,
    onShowLeads,
    onShowLeadsBySource,
    onShowCalls,
    onShowConversations,
    onShowOpportunities,
    onShowHandoffs,
    onShowClaimedHandoffs,
    onShowReturnedHandoffs,
    onShowDeclinedHandoffs,
    onShowExpiredHandoffs,
    onShowLiveTransfers,
    onShowEmailOpens,
    onShowEmailEngagement,
    onShowWebsiteStats,
    onShowPropertyViews,
    onShowLeadCaptures,
    onShowPageViews,
    onShowFlywheelFollowUps,
  } = config;

  // SMS is a flywheel-only channel. Non-flywheel orgs see no SMS anywhere, so
  // it must not reach the Remi Touches widget either — not in the headline
  // total, not as a clickable sub-stat (which opens the org-wide SMS
  // drill-down), and not in the Add-Widget preview.
  const smsVisibleTotal = flywheelEnabled ? smsTotal : 0;

  return {
    flywheel: (() => {
      const totalFollowUps = followUpData?.total || 0;
      const canDrillDown = Boolean(onShowFlywheelFollowUps);
      const drilldown = (filter: FlywheelDrilldownFilter, count: number) =>
        canDrillDown && count > 0 ? () => onShowFlywheelFollowUps?.(filter) : undefined;
      const typeSubStats =
        followUpData?.types.map((type) => ({
          label: type.label,
          value: type.count,
          onClick: drilldown(
            {
              kind: 'followups',
              title: `${type.label} Follow-ups`,
              decisionType: type.decisionType,
            },
            type.count
          ),
        })) ?? [];
      const contactsCount = followUpData?.uniqueContacts || 0;
      const contactsSubStat = {
        label: 'Contacts',
        value: contactsCount,
        onClick: drilldown({ kind: 'contacts', title: 'Contacts in Flywheel' }, contactsCount),
      };

      return {
        card: {
          id: 'flywheel',
          // The follow-up count is Remi's live queue of future touches; completed,
          // skipped and past work are excluded server-side.
          label: 'Flywheel',
          valueLabel: (
            <span title="Follow-ups Remi has scheduled that haven't happened yet">
              Upcoming Follow-ups
            </span>
          ),
          description:
            followUpData?.total && followUpData.timeSaved ? (
              <>
                <Clock className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                Saving you{' '}
                <span className="text-primaryfortext font-semibold">{followUpData.timeSaved}</span>
              </>
            ) : undefined,
          value: totalFollowUps,
          isLoading: isDataLoading || followUpLoading,
          icon: <Zap className="w-5 h-5" />,
          isClickable: totalFollowUps > 0 && canDrillDown,
          onClick: drilldown(
            { kind: 'followups', title: 'Scheduled Flywheel Follow-ups' },
            totalFollowUps
          ),
          subStats: [contactsSubStat, ...typeSubStats],
        },
        preview: (
          <PreviewContent>
            <PreviewRow label="Upcoming Follow-ups" value={totalFollowUps} />
            <PreviewRow label="Contacts" value={contactsCount} />
            {followUpData?.types.map((type) => (
              <PreviewRow key={type.decisionType} label={type.label} value={type.count} />
            ))}
            {followUpData?.timeSaved && (
              <PreviewRow label="Time Saved" value={followUpData.timeSaved} />
            )}
          </PreviewContent>
        ),
      };
    })(),
    leads: {
      card: {
        id: 'leads',
        label: 'New Leads',
        value: dashboardData?.stats.leads.value || 0,
        isLoading: isDataLoading,
        highlighted: true,
        icon: <UserPlus className="w-5 h-5" />,
        isClickable: (dashboardData?.stats.leads.value || 0) > 0,
        onClick: onShowLeads,
        subStats: [
          {
            label: 'Email',
            value: dashboardData?.stats.leads.email || 0,
            onClick: () => onShowLeadsBySource('Email'),
          },
          {
            label: 'Organic',
            value: dashboardData?.stats.leads.website || 0,
            onClick: () => onShowLeadsBySource('Organic'),
          },
          {
            label: 'PPC',
            value: dashboardData?.stats.leads.ppc || 0,
            onClick: () => onShowLeadsBySource('PPC'),
          },
        ].filter((s) => s.value !== 0),
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Email Leads" value={dashboardData?.stats.leads.email || 0} />
          <PreviewRow label="Organic Leads" value={dashboardData?.stats.leads.website || 0} />
          <PreviewRow label="PPC Leads" value={dashboardData?.stats.leads.ppc || 0} />
        </PreviewContent>
      ),
    },
    calls: {
      card: {
        id: 'calls',
        label: 'Calls',
        // Flywheel mode → headline Conversations (Opportunities is hidden
        // from the dashboard entirely). Otherwise keep Opportunities as
        // the main value with Conversations + Calls Made as sub-stats.
        valueLabel: flywheelEnabled ? 'Conversations' : 'Opportunities',
        value: flywheelEnabled ? conversations : opportunityCallsTotal,
        isLoading: isDataLoading || opportunityCallsLoading,
        icon: <Phone className="w-5 h-5" />,
        isClickable: flywheelEnabled ? conversations > 0 : opportunityCallsTotal > 0,
        onClick: flywheelEnabled ? onShowConversations : onShowOpportunityCalls,
        subStats: (flywheelEnabled
          ? [
              {
                label: 'Calls Made',
                value: dashboardData?.stats.calls || 0,
                onClick: (dashboardData?.stats.calls || 0) > 0 ? onShowCalls : undefined,
              },
            ]
          : [
              {
                label: 'Calls Made',
                value: dashboardData?.stats.calls || 0,
                onClick: (dashboardData?.stats.calls || 0) > 0 ? onShowCalls : undefined,
              },
              {
                label: 'Conversations',
                value: conversations,
                onClick: conversations > 0 ? onShowConversations : undefined,
              },
            ]
        ).filter((s) => s.value !== 0),
      },
      preview: (
        <PreviewContent>
          {flywheelEnabled ? (
            <>
              <PreviewRow label="Conversations" value={conversations || 0} />
              <PreviewRow label="Calls Made" value={dashboardData?.stats.calls || 0} />
            </>
          ) : (
            <>
              <PreviewRow label="Opportunities" value={opportunityCallsTotal} />
              <PreviewRow label="Calls Made" value={dashboardData?.stats.calls || 0} />
              <PreviewRow label="Conversations" value={conversations || 0} />
            </>
          )}
        </PreviewContent>
      ),
    },
    opportunities: {
      card: {
        id: 'opportunities',
        label: 'Opportunities',
        value: dashboardData?.stats.opportunities?.value || opportunities || 0,
        isLoading: isDataLoading,
        icon: <Target className="w-5 h-5" />,
        isClickable: (dashboardData?.stats.opportunities?.value || opportunities || 0) > 0,
        onClick: onShowOpportunities,
        subStats: [
          {
            label: 'Conversations',
            value: conversations,
            onClick: conversations > 0 ? onShowConversations : undefined,
          },
        ].filter((s) => s.value !== 0),
      },
      preview: (
        <PreviewContent>
          <PreviewRow
            label="Opportunities"
            value={dashboardData?.stats.opportunities?.value || opportunities || 0}
          />
          <PreviewRow label="Conversations" value={conversations || 0} />
        </PreviewContent>
      ),
    },
    handoffs: {
      card: {
        id: 'handoffs',
        label: (
          <>
            Handoffs{' '}
            <span className="text-muted-foreground/60 font-normal normal-case text-xs">
              (All Time)
            </span>
          </>
        ),
        value: handoffStats.pending,
        isLoading: isDataLoading || handoffLoading,
        icon: <Flame className="w-5 h-5" />,
        isClickable: handoffStats.pending > 0,
        onClick: onShowHandoffs,
        subStats: [
          { label: 'Claimed', value: handoffStats.claimed || 0, onClick: onShowClaimedHandoffs },
          {
            label: 'Returned',
            value: handoffStats.released || 0,
            onClick: onShowReturnedHandoffs,
          },
          {
            label: 'Declined',
            value: handoffStats.declined || 0,
            onClick: onShowDeclinedHandoffs,
          },
          {
            label: 'Expired',
            value: handoffStats.expired || 0,
            onClick: onShowExpiredHandoffs,
          },
        ],
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Pending" value={handoffStats.pending} />
          <PreviewRow label="Claimed" value={handoffStats.claimed} />
          <PreviewRow label="Returned to Remi" value={handoffStats.released} />
          <PreviewRow label="Declined" value={handoffStats.declined} />
          <PreviewRow label="Expired" value={handoffStats.expired} />
        </PreviewContent>
      ),
    },
    live_transfers: {
      card: {
        id: 'live_transfers',
        label: 'Live Transfers',
        value: liveTransfersTotal,
        isLoading: liveTransfersLoading,
        icon: <PhoneForwarded className="w-5 h-5" />,
        isClickable: liveTransfersTotal > 0,
        onClick: onShowLiveTransfers,
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Live Transfers" value={liveTransfersTotal} />
        </PreviewContent>
      ),
    },
    email: {
      card: {
        id: 'email',
        label: 'Emails',
        value: emailDeliveredTotal,
        valueLabel: 'Delivered',
        isLoading: isDataLoading,
        icon: <Mail className="w-5 h-5" />,
        isClickable: emailDeliveredTotal > 0,
        onClick: onShowEmailOpens,
        subStats: [
          ...(emailOpensTotal > 0
            ? [
                {
                  label: 'Opens',
                  value: emailOpensTotal,
                  onClick: onShowEmailEngagement.bind(null, 'open'),
                },
              ]
            : []),
          ...(emailClicksTotal > 0
            ? [
                {
                  label: 'Clicks',
                  value: emailClicksTotal,
                  onClick: onShowEmailEngagement.bind(null, 'click'),
                },
              ]
            : []),
        ],
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Delivered" value={emailDeliveredTotal} />
          <PreviewRow label="Opens" value={emailOpensTotal} />
          <PreviewRow label="Clicks" value={emailClicksTotal} />
        </PreviewContent>
      ),
    },
    website: {
      card: {
        id: 'website',
        label: 'Website',
        valueLabel: 'Interactions',
        value: webActivitiesTotal || 0,
        isLoading: isDataLoading || webActivitiesLoading,
        icon: <Globe className="w-5 h-5" />,
        isClickable: (webActivitiesTotal || 0) > 0,
        onClick: onShowWebsiteStats,
        subStats: [
          ...(propertyViewsTotal > 0
            ? [{ label: 'Property Views', value: propertyViewsTotal, onClick: onShowPropertyViews }]
            : []),
          ...(leadCapturesTotal > 0
            ? [{ label: 'Conversions', value: leadCapturesTotal, onClick: onShowLeadCaptures }]
            : []),
        ],
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Traffic" value={webActivitiesTotal || 0} />
        </PreviewContent>
      ),
    },

    // ── GQL-powered widgets ───────────────────────────────────────────

    page_views: {
      card: {
        id: 'page_views',
        label: 'Page Views',
        value: orgStats?.totalPageViews || 0,
        isLoading: orgStatsLoading,
        icon: <MousePointer className="w-5 h-5" />,
        isClickable: (orgStats?.totalPageViews || 0) > 0,
        onClick: onShowPageViews,
        subStats: [
          { label: 'Properties', value: orgStats?.totalPropertyViews || 0 },
          { label: 'Search Results', value: orgStats?.totalSearchPageViews || 0 },
          { label: 'Home Valuations', value: orgStats?.totalHomeValuations || 0 },
          { label: 'Saved Properties', value: orgStats?.totalSavedProperties || 0 },
          { label: 'Email Link Views', value: orgStats?.totalEmailPropertyLinkViews || 0 },
        ].filter((s) => s.value !== 0),
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Total Page Views" value={orgStats?.totalPageViews || 0} />
          <PreviewRow label="Properties" value={orgStats?.totalPropertyViews || 0} />
          <PreviewRow label="Search Results" value={orgStats?.totalSearchPageViews || 0} />
          <PreviewRow label="Home Valuations" value={orgStats?.totalHomeValuations || 0} />
          <PreviewRow label="Saved Properties" value={orgStats?.totalSavedProperties || 0} />
          <PreviewRow label="Email Link Views" value={orgStats?.totalEmailPropertyLinkViews || 0} />
        </PreviewContent>
      ),
    },

    // COMMENTED OUT — Active Users widget (re-add later)
    // active_users: {
    //   card: {
    //     id: 'active_users',
    //     label: <>Active Users <span className="text-muted-foreground/60 font-normal normal-case text-xs">(All Time)</span></>,
    //     value: orgStats?.uniqueActiveUsers || 0,
    //     isLoading: orgStatsLoading,
    //     icon: <Users className="w-5 h-5" />,
    //     isClickable: (orgStats?.uniqueActiveUsers || 0) > 0,
    //     onClick: onShowActiveUsers,
    //     subStats: [
    //       { label: 'Answering Calls', value: orgStats?.uniqueContactsCallsAnswered || 0, onClick: () => onShowActiveUsers('answeredCalls') },
    //       { label: 'Opening Emails', value: orgStats?.uniqueContactsEmailsOpened || 0, onClick: () => onShowActiveUsers('openedEmails') },
    //       { label: 'Clicking Emails', value: orgStats?.uniqueContactsEmailsClicked || 0, onClick: () => onShowActiveUsers('clickedEmails') },
    //       { label: 'Clicking Properties', value: orgStats?.uniqueContactsPropertyClicked || 0, onClick: () => onShowActiveUsers('clickedProperties') },
    //     ].filter((s) => s.value !== 0),
    //   },
    //   preview: (
    //     <PreviewContent>
    //       <PreviewRow label="Unique Active Users" value={orgStats?.uniqueActiveUsers || 0} />
    //       <PreviewRow label="Answering Calls" value={orgStats?.uniqueContactsCallsAnswered || 0} />
    //       <PreviewRow label="Opening Emails" value={orgStats?.uniqueContactsEmailsOpened || 0} />
    //       <PreviewRow label="Clicking Emails" value={orgStats?.uniqueContactsEmailsClicked || 0} />
    //       <PreviewRow label="Clicking Properties" value={orgStats?.uniqueContactsPropertyClicked || 0} />
    //       <PreviewRow label="Searching" value={orgStats?.uniqueContactsSearching || 0} />
    //     </PreviewContent>
    //   ),
    // },

    remi_touches: {
      card: {
        id: 'remi_touches',
        label: 'Remi Touches',
        value:
          (dashboardData?.stats.calls || 0) +
          smsVisibleTotal +
          (webActivitiesTotal || 0) +
          emailDeliveredTotal,
        isLoading: isDataLoading || webActivitiesLoading || smsLoading,
        icon: <Bot className="w-5 h-5" />,
        subStats: [
          ...(dashboardData?.stats.calls
            ? [{ label: 'Calls', value: dashboardData.stats.calls, onClick: onShowCalls }]
            : []),
          ...(smsVisibleTotal
            ? [{ label: 'SMS', value: smsVisibleTotal, onClick: onShowSms }]
            : []),
          ...(webActivitiesTotal
            ? [{ label: 'Website', value: webActivitiesTotal, onClick: onShowWebsiteStats }]
            : []),
          ...(emailDeliveredTotal
            ? [{ label: 'Emails', value: emailDeliveredTotal, onClick: onShowEmailOpens }]
            : []),
        ],
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Calls" value={dashboardData?.stats.calls || 0} />
          {flywheelEnabled && <PreviewRow label="SMS" value={smsTotal} />}
          <PreviewRow label="Website" value={webActivitiesTotal || 0} />
          <PreviewRow label="Emails" value={emailDeliveredTotal} />
        </PreviewContent>
      ),
    },

    // SMS — live counts from organization.messages(channel:"sms") (SPEC-1706).
    sms: {
      card: {
        id: 'sms',
        label: 'SMS',
        value: smsTotal,
        isLoading: smsLoading,
        icon: <MessageSquare className="w-5 h-5" />,
        description: 'Text messages sent and received by Remi',
        isClickable: smsTotal > 0,
        onClick: onShowSms,
        // COMMENTED OUT — "SMS sent" is an all-time count (every Remi SMS since
        // Flywheel was enabled) and ignores the dashboard date range, so it
        // reads as inconsistent next to the date-windowed total. Re-add once
        // follow-up stats can be windowed.
        // subStats: followUpData?.outboundSms
        //   ? [{ label: 'SMS sent', value: followUpData.outboundSms }]
        //   : undefined,
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Total" value={smsTotal} />
        </PreviewContent>
      ),
    },

    // ── PREVIEW: Appointments — no live aggregate yet (no GQL appointment counts).
    appointments: {
      card: {
        id: 'appointments',
        label: 'Appointments',
        value: 0,
        isLoading: false,
        icon: <CalendarCheck className="w-5 h-5" />,
        description: 'Coming soon — appointment tracking not yet wired up.',
      },
      preview: (
        <PreviewContent>
          <PreviewRow label="Appointments" value={0} />
        </PreviewContent>
      ),
    },

    // COMMENTED OUT — Nurture Pipeline widget (re-add later)
    // nurture: {
    //   card: {
    //     id: 'nurture',
    //     label: <>Nurture Pipeline <span className="text-muted-foreground/60 font-normal normal-case text-xs">(All Time)</span></>,
    //     value: (orgStats?.clientsInCallNurture || 0) + (orgStats?.clientsInEmailNurture || 0),
    //     isLoading: orgStatsLoading,
    //     icon: <Layers className="w-5 h-5" />,
    //     isClickable: ((orgStats?.clientsInCallNurture || 0) + (orgStats?.clientsInEmailNurture || 0)) > 0,
    //     onClick: () => onShowNurture?.(),
    //     subStats: [
    //       { label: 'Call Nurture', value: orgStats?.clientsInCallNurture || 0, onClick: (orgStats?.clientsInCallNurture || 0) > 0 ? () => onShowNurture?.('callNurture') : undefined },
    //       { label: 'Email Nurture', value: orgStats?.clientsInEmailNurture || 0, onClick: (orgStats?.clientsInEmailNurture || 0) > 0 ? () => onShowNurture?.('emailNurture') : undefined },
    //     ].filter((s) => s.value !== 0),
    //   },
    //   preview: (
    //     <PreviewContent>
    //       <PreviewRow label="In Call Nurture" value={orgStats?.clientsInCallNurture || 0} />
    //       <PreviewRow label="In Email Nurture" value={orgStats?.clientsInEmailNurture || 0} />
    //     </PreviewContent>
    //   ),
    // },

    // COMMENTED OUT — Search Alerts widget (re-add later)
    // search_alerts: {
    //   card: {
    //     id: 'search_alerts',
    //     label: <>Search Alerts <span className="text-muted-foreground/60 font-normal normal-case text-xs">(All Time)</span></>,
    //     value: orgStats?.totalSearchAlertsContacts || 0,
    //     valueLabel: 'On E-Alerts',
    //     isLoading: orgStatsLoading,
    //     icon: <Bell className="w-5 h-5" />,
    //     isClickable: (orgStats?.totalSearchAlertsContacts || 0) > 0,
    //     onClick: () => onShowSearchAlerts?.(),
    //     subStats: [
    //       { label: 'Searches Made', value: orgStats?.totalSearchesMadeByUsers || 0, onClick: (orgStats?.totalSearchesMadeByUsers || 0) > 0 ? () => onShowSearchAlerts?.('searches') : undefined },
    //     ].filter((s) => s.value !== 0),
    //   },
    //   preview: (
    //     <PreviewContent>
    //       <PreviewRow label="Alert Contacts" value={orgStats?.totalSearchAlertsContacts || 0} />
    //       <PreviewRow label="Searches Made" value={orgStats?.totalSearchesMadeByUsers || 0} />
    //     </PreviewContent>
    //   ),
    // },
  };
}

export function buildPinnedWidgetMap(
  pinnedWidgets: PinnedWidget[]
): Record<string, { card: StatCardProps; preview: ReactNode }> {
  const map: Record<string, { card: StatCardProps; preview: ReactNode }> = {};
  for (const pw of pinnedWidgets) {
    const addedAt = new Date(pw.pinnedAt).toLocaleDateString();
    const baseDescription = pw.block.delta
      ? `${pw.block.delta}${pw.block.timeframe ? ` · ${pw.block.timeframe}` : ''}`
      : pw.block.timeframe || undefined;
    const description = baseDescription
      ? `${baseDescription} · Added ${addedAt}`
      : `Added ${addedAt}`;
    map[pw.id] = {
      card: {
        id: pw.id,
        label: pw.block.title,
        description,
        value: typeof pw.block.value === 'number' ? pw.block.value : 0,
        isLoading: false,
        icon: <Zap className="w-5 h-5" />,
      },
      preview: (
        <PreviewContent>
          <PreviewRow
            label="Value"
            value={typeof pw.block.value === 'number' ? pw.block.value : 0}
          />
          {pw.block.source && <PreviewRow label="Source" value={pw.block.source} />}
          {pw.block.timeframe && <PreviewRow label="Timeframe" value={pw.block.timeframe} />}
          <PreviewRow label="Added" value={addedAt} />
        </PreviewContent>
      ),
    };
  }
  return map;
}
